import { prefersReducedMotion } from "@theme/utilities";

/**
 * Generic custom element that sets `data-animate="true"` when scrolled into view.
 *
 * Attributes:
 * - `data-animate-threshold` – IntersectionObserver threshold (0–1, default 0.1)
 * - `data-animate-margin` – IntersectionObserver rootMargin (default "0px"). Use a
 *   negative bottom (e.g. "0px 0px -20% 0px") to delay the trigger until the element
 *   is genuinely in the viewport rather than a sliver at the bottom edge.
 * - `data-animate-once` – if present, fires once then disconnects; otherwise toggles
 *
 * At least one of these attributes must be present for the observer to activate
 * (ignored when prefers-reduced-motion is set — the final state is shown immediately).
 *
 * By default the host element is the intersection target. For a tall section
 * whose key content sits lower down (e.g. a stacked 50/50 where the text is
 * below the image), mark that inner element with `data-animate-trigger` and the
 * observer watches it instead — so the reveal fires when the content itself is
 * in the viewport, not when the section's top edge first appears. `data-animate`
 * is still set on the host (the `group`).
 */
class ScrollAnimate extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  _observer = null;

  connectedCallback() {
    if (prefersReducedMotion()) {
      this.dataset.animate = "true";
      return;
    }

    if (
      this.hasAttribute("data-animate-once") ||
      this.hasAttribute("data-animate-threshold") ||
      this.hasAttribute("data-animate-margin")
    ) {
      this._setupScrollObserver();
    }
  }

  disconnectedCallback() {
    this._observer?.disconnect();
  }

  _setupScrollObserver() {
    const threshold = parseFloat(this.dataset.animateThreshold) || 0.1;
    const rootMargin = this.dataset.animateMargin || "0px";
    const once = this.hasAttribute("data-animate-once");
    const target = this.querySelector("[data-animate-trigger]") || this;

    if (once) {
      this._observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            this.dataset.animate = "true";
            this._observer?.disconnect();
          }
        },
        { threshold, rootMargin },
      );
    } else {
      this._observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            this.dataset.animate = "true";
          } else if (
            !entry.isIntersecting ||
            entry.intersectionRatio < threshold / 2
          ) {
            this.dataset.animate = "false";
          }
        },
        { threshold: [0, threshold], rootMargin },
      );
    }

    this._observer.observe(target);
  }
}

if (!customElements.get("scroll-animate")) {
  customElements.define("scroll-animate", ScrollAnimate);
}
